import { useState, useEffect, useRef } from 'react';
import { Crosshair, Lock, Clock, Trophy, Users, ChevronDown, ChevronUp, Check, AlertCircle } from 'lucide-react';
import {
  PredictionAnswer,
  PredictionGame,
  lockAnswerSong,
  confirmAndDeploy,
  revealGame,
  completeGame,
  cancelGame,
  decrementCountdown,
  subscribeToNowPlaying,
  subscribeToPredictionAnswer,
} from '../../services/prediction';
import type { SongRequest } from '../../services/requests';
import type { Event } from '../../services/events';
import toast from 'react-hot-toast';

interface PredictionGameBannerProps {
  game: PredictionGame;
  setEvent: React.Dispatch<React.SetStateAction<Event | null>>;
  queuedRequests: SongRequest[];
}

export default function PredictionGameBanner({ game, setEvent, queuedRequests }: PredictionGameBannerProps) {
  const [expanded, setExpanded] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const prevNowPlayingRef = useRef<string | null>(null);
  const hasAutoLockedRef = useRef(false);
  const [answer, setAnswer] = useState<PredictionAnswer | null>(null);

  useEffect(() => subscribeToPredictionAnswer(game.id, setAnswer), [game.id]);

  const answerSong = queuedRequests.find(r => r.id === answer?.requestId);
  const firstQueuedSong = queuedRequests[0];

  // Auto-detect Now Playing changes to decrement countdown
  useEffect(() => {
    if (game.status !== 'countdown') return;

    const unsub = subscribeToNowPlaying(game.eventId, (nowPlayingId) => {
      if (prevNowPlayingRef.current !== null && nowPlayingId !== null && nowPlayingId !== prevNowPlayingRef.current) {
        decrementCountdown(game.id).catch(() => {});
      }
      prevNowPlayingRef.current = nowPlayingId;
    });

    return () => unsub();
  }, [game.status, game.id, game.eventId]);

  // Auto-lock first queued song when entering ready state
  useEffect(() => {
    if (game.status !== 'ready' || game.answerLocked || hasAutoLockedRef.current || !firstQueuedSong) return;
    hasAutoLockedRef.current = true;
    lockAnswerSong(game.id, firstQueuedSong.id).catch((error) => {
      console.error('[prediction] Auto-lock of answer song failed', error);
      toast.error('Could not lock the answer song. Choose one under "Pick a different song".');
      hasAutoLockedRef.current = false;
    });
  }, [game.status, game.answerLocked, game.id, firstQueuedSong]);

  // Reset auto-lock ref when game changes
  useEffect(() => {
    if (game.status !== 'ready') {
      hasAutoLockedRef.current = false;
    }
  }, [game.status]);

  const handleDeploy = async () => {
    if (deploying) return;
    setDeploying(true);
    try {
      await confirmAndDeploy(game.id);
      toast.success('Game deployed! Attendees are playing.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to deploy');
    } finally {
      setDeploying(false);
    }
  };

  const handleReveal = async () => {
    if (revealing) return;
    setRevealing(true);
    try {
      await revealGame(game.id);
      toast.success('Winners revealed!');
    } catch {
      toast.error('Failed to reveal');
    } finally {
      setRevealing(false);
    }
  };

  const handleComplete = async () => {
    try {
      await completeGame(game.id);
      setEvent(prev => prev ? {
        ...prev,
        settings: { ...prev.settings, hideRankingInfo: game.previousHideRankingInfo, predictionGameActive: false, predictionGameId: undefined },
      } : prev);
      toast.success('Game completed!');
    } catch {
      toast.error('Failed to complete');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelGame(game.id);
      setEvent(prev => prev ? {
        ...prev,
        settings: { ...prev.settings, hideRankingInfo: game.previousHideRankingInfo, predictionGameActive: false, predictionGameId: undefined },
      } : prev);
      toast.success('Game cancelled');
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const handleManualDecrement = async () => {
    try {
      await decrementCountdown(game.id);
    } catch {
      toast.error('Failed to decrement');
    }
  };

  const handleChangeSong = async (requestId: string) => {
    try {
      await lockAnswerSong(game.id, requestId);
      toast.success('Answer song changed!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to change song');
    }
  };

  const statusColor = {
    countdown: 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20',
    ready: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20',
    active: 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20',
    revealing: 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20',
    completed: '',
    cancelled: '',
  }[game.status];

  return (
    <div className={`rounded-lg border-2 ${statusColor} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2"
      >
        <div className="flex items-center gap-2">
          <Crosshair className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Prediction Game</span>
          {game.status === 'countdown' && (
            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {game.songsRemaining} song{game.songsRemaining !== 1 ? 's' : ''} away
            </span>
          )}
          {game.status === 'ready' && (
            <span className="text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded font-medium">
              Confirm & Deploy
            </span>
          )}
          {game.status === 'active' && (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-xs text-green-600 dark:text-green-400">
                <Users className="w-3 h-3 inline mr-0.5" />{game.playerCount}
              </span>
            </>
          )}
          {game.status === 'revealing' && (
            <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
              <Trophy className="w-3 h-3 inline mr-0.5" />
              {game.winnerNicknames.length > 0 ? game.winnerNicknames.join(', ') : 'No winner'}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">

          {/* Countdown */}
          {game.status === 'countdown' && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Auto-detects when a song changes. Countdown decrements automatically.
              </p>
              <button
                onClick={handleManualDecrement}
                className="text-xs text-blue-600 dark:text-blue-400 underline hover:no-underline"
              >
                Manual: mark song finished
              </button>
              <button onClick={handleCancel} className="w-full px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs hover:bg-red-200">
                Cancel Game
              </button>
            </>
          )}

          {/* Ready — confirm and deploy */}
          {game.status === 'ready' && (
            <>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Is this the next song?</p>
              {answerSong ? (
                <div className="flex items-center gap-2 px-2 py-2 bg-white dark:bg-gray-800 rounded border border-amber-200 dark:border-amber-700">
                  {answerSong.song.albumArtUrl && (
                    <img src={answerSong.song.albumArtUrl} alt="" className="w-10 h-10 rounded flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{answerSong.song.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{answerSong.song.artist}</p>
                  </div>
                  <Lock className="w-4 h-4 text-green-600 flex-shrink-0" />
                </div>
              ) : (
                <div className="flex items-center gap-2 px-2 py-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  No queued songs — queue a song first
                </div>
              )}

              {answerSong && !answerSong.queuedInPlayer && (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded text-xs text-yellow-700 dark:text-yellow-300">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  This song isn't queued in your player yet
                </div>
              )}

              {/* Pick different song */}
              {queuedRequests.length > 1 && (
                <details className="text-xs">
                  <summary className="text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                    Pick a different song
                  </summary>
                  <div className="mt-1 space-y-1 max-h-28 overflow-y-auto">
                    {queuedRequests.filter(r => r.id !== answer?.requestId).map(r => (
                      <button
                        key={r.id}
                        onClick={() => handleChangeSong(r.id)}
                        className="w-full flex items-center gap-2 px-2 py-1 bg-gray-50 dark:bg-gray-800/50 rounded text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <span className="truncate text-gray-900 dark:text-white">{r.song.title}</span>
                        <span className="text-gray-400 truncate flex-shrink-0">— {r.song.artist}</span>
                      </button>
                    ))}
                  </div>
                </details>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleDeploy}
                  disabled={!game.answerLocked || deploying}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-primary text-white rounded text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  {deploying ? 'Deploying...' : 'Confirm & Deploy'}
                </button>
                <button onClick={handleCancel} className="px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-sm hover:bg-red-200">
                  Cancel
                </button>
              </div>
            </>
          )}

          {/* Active — live stats */}
          {game.status === 'active' && (
            <>
              <div className="grid grid-cols-5 gap-1 text-center">
                {[
                  { label: 'R1', value: game.round1Survivors, total: game.playerCount },
                  { label: 'R2', value: game.round2Survivors, total: game.round1Survivors },
                  { label: 'R3', value: game.round3Survivors, total: game.round2Survivors },
                  { label: 'Final', value: game.round4Survivors, total: game.round3Survivors },
                  { label: 'Total', value: game.playerCount, total: null },
                ].map(({ label, value, total }) => (
                  <div key={label} className="px-1 py-1 bg-white dark:bg-gray-800 rounded">
                    <p className="text-[10px] text-gray-500">{label}</p>
                    <p className="text-xs font-bold text-gray-900 dark:text-white">
                      {total !== null ? `${value}/${total || '-'}` : value}
                    </p>
                  </div>
                ))}
              </div>

              {answerSong && (
                <div className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-800 rounded text-xs">
                  <Lock className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 truncate">{answerSong.song.title} — {answerSong.song.artist}</span>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleReveal}
                  disabled={revealing}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  <Trophy className="w-3 h-3" /> {revealing ? 'Revealing...' : 'Reveal (song is playing)'}
                </button>
                <button onClick={handleCancel} className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs hover:bg-red-200">
                  Cancel
                </button>
              </div>
            </>
          )}

          {/* Revealing — winners */}
          {game.status === 'revealing' && (
            <>
              {game.winnerNicknames.length > 0 ? (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-white dark:bg-gray-800 rounded text-xs">
                  <Trophy className="w-3 h-3 text-amber-500" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    Winner: {game.winnerNicknames.join(', ')}
                  </span>
                </div>
              ) : (
                <p className="text-xs text-gray-500">No one picked correctly — no winner.</p>
              )}
              <button
                onClick={handleComplete}
                className="w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90"
              >
                Complete Game
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
